import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ShieldCheck, Zap, Wallet } from "lucide-react";
import { DownloadButton } from "./DownloadButtons";
import { APP_SCREENS, scrollToId } from "@/lib/leadzi";

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.85, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MaskLine = ({ children, i }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span variants={lineReveal} custom={i} initial="hidden" animate="show" className="block">
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 15 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 15 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section id="top" className="relative overflow-hidden bg-white pt-28 lg:pt-36">
      {/* backdrop */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/2 top-[-10%] h-[520px] w-[720px] -translate-x-1/2 rounded-full bg-brand/10 blur-[120px]" />
        <div className="absolute right-[-5%] top-[30%] h-[360px] w-[360px] rounded-full bg-brand/5 blur-[100px]" />
        <div
          className="absolute inset-0 opacity-[0.6]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(13,15,18,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(13,15,18,0.04) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage: "radial-gradient(ellipse 80% 60% at 50% 0%, black 40%, transparent 85%)",
          }}
        />
      </div>

      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 pb-20 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:gap-8 lg:px-8 lg:pb-28">
        {/* Left */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand-light px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-brand-700"
          >
            <Zap className="h-3.5 w-3.5 fill-brand" /> Pour les commerciaux du secteur CHR
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-display text-4xl font-800 font-extrabold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl"
          >
            <MaskLine i={0}>Ton client a un</MaskLine>
            <MaskLine i={1}>besoin que tu ne</MaskLine>
            <MaskLine i={2}>
              peux <span className="text-brand">pas traiter&nbsp;?</span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-6 max-w-xl text-lg leading-relaxed text-ink/60 sm:text-xl"
          >
            Ne laisse pas passer l'opportunité. Dépose-la sur Leadzi et valorise ton
            contact. Nos équipes analysent le besoin et{" "}
            <span className="font-semibold text-ink">te rémunère.</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <DownloadButton testId="hero-download-cta" className="px-7 py-3.5 text-base" />
            <button
              data-testid="hero-demo-cta"
              onClick={() => scrollToId("how")}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-black/10 bg-white px-7 py-3.5 text-base font-bold text-ink transition-colors hover:border-brand/40 hover:text-brand"
            >
              Comment ça marche ?
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm font-semibold text-ink/55"
          >
            <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand" /> Confidentiel</span>
            <span className="inline-flex items-center gap-2"><Zap className="h-4 w-4 text-brand" /> Dépôt en 45 sec</span>
            <span className="inline-flex items-center gap-2"><Wallet className="h-4 w-4 text-brand" /> Rémunération directe</span>
          </motion.div>
        </div>

        {/* Right : phone */}
        <motion.div
          ref={ref}
          onMouseMove={onMove}
          onMouseLeave={onLeave}
          initial={{ opacity: 0, scale: 0.92, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          style={{ perspective: 1200 }}
          className="relative mx-auto hidden w-full max-w-sm lg:block"
        >
          <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
            {/* glow */}
            <div className="absolute -inset-6 -z-10 rounded-[3rem] bg-brand/20 blur-3xl" />
            {/* phone frame */}
            <div className="relative overflow-hidden rounded-[2.75rem] border-[10px] border-ink bg-ink shadow-[0_40px_80px_-24px_rgba(13,15,18,0.55)]">
              <div className="absolute left-1/2 top-0 z-10 h-6 w-32 -translate-x-1/2 rounded-b-2xl bg-ink" />
              <img src={APP_SCREENS.newOpportunity} alt="Application Leadzi CHR — nouvelle opportunité" className="block w-full" />
            </div>

            {/* floating status card */}
            <motion.div
              initial={{ opacity: 0, y: 20, x: 20 }}
              animate={{ opacity: 1, y: 0, x: 0 }}
              transition={{ duration: 0.7, delay: 1.1 }}
              style={{ transform: "translateZ(60px)" }}
              className="absolute -right-4 top-16 w-52 rounded-2xl border border-black/5 bg-white p-4 shadow-2xl sm:-right-8"
            >
              <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-brand">Nouvelle opportunité</div>
              <div className="text-sm font-bold text-ink">Restaurant Le Patio</div>
              <div className="mt-1 text-xs text-ink/50">Besoin · Solution de caisse</div>
              <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-600">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Opportunité envoyée
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
